/**
 * /mcp-usage ルートの URL search params 同期ロジック。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business MCP 利用量 dashboard の URL search params を sparse 表現で復元・正規化する pure functions。default 値は URL に出さず、不正な値は default にフォールバックする
 * @graph-connects none
 */

import type { McpUsageGranularity, McpUsageRange, McpUsageScope } from '../types/api.js';

/** @graph-connects none */
export const MCP_USAGE_SCOPE_VALUES = [
  'overall',
  'team',
  'member',
] as const satisfies readonly McpUsageScope[];

/** @graph-connects none */
export const MCP_USAGE_RANGE_VALUES = [
  '7d',
  '14d',
  '30d',
  '90d',
  '180d',
  '365d',
] as const satisfies readonly Exclude<McpUsageRange, 'custom'>[];

/** @graph-connects none */
export const MCP_USAGE_GRANULARITY_VALUES = [
  'day',
  'week',
  'month',
] as const satisfies readonly McpUsageGranularity[];

/** @graph-connects none */
export interface McpUsageSearch {
  scope?: McpUsageScope;
  range?: McpUsageRange;
  granularity?: McpUsageGranularity;
  team?: string;
  email?: string;
}

/** @graph-connects none */
export const MCP_USAGE_SEARCH_DEFAULTS = {
  scope: 'overall' as McpUsageScope,
  range: '30d' as McpUsageRange,
  granularity: 'day' as McpUsageGranularity,
};

/**
 * URL search を復元する。不正値は黙って undefined にし、既定値と一致する値も undefined に潰す。
 * @graph-connects none
 */
export function parseMcpUsageSearch(raw: Record<string, unknown>): McpUsageSearch {
  const rawScope = raw['scope'];
  const scope =
    typeof rawScope === 'string' &&
    (MCP_USAGE_SCOPE_VALUES as readonly string[]).includes(rawScope) &&
    rawScope !== MCP_USAGE_SEARCH_DEFAULTS.scope
      ? (rawScope as McpUsageScope)
      : undefined;
  const rawRange = raw['range'];
  const range =
    typeof rawRange === 'string' &&
    (MCP_USAGE_RANGE_VALUES as readonly string[]).includes(rawRange) &&
    rawRange !== MCP_USAGE_SEARCH_DEFAULTS.range
      ? (rawRange as McpUsageRange)
      : undefined;
  const rawGranularity = raw['granularity'];
  const granularity =
    typeof rawGranularity === 'string' &&
    (MCP_USAGE_GRANULARITY_VALUES as readonly string[]).includes(rawGranularity) &&
    rawGranularity !== MCP_USAGE_SEARCH_DEFAULTS.granularity
      ? (rawGranularity as McpUsageGranularity)
      : undefined;
  const team = typeof raw['team'] === 'string' && raw['team'].length > 0 ? raw['team'] : undefined;
  const email =
    typeof raw['email'] === 'string' && raw['email'].length > 0 ? raw['email'] : undefined;
  return { scope, range, granularity, team, email };
}

/**
 * sparse search を完全形に戻して default を埋めた値を取り出す。
 * @graph-connects none
 */
export function resolveMcpUsageSearch(search: McpUsageSearch): {
  scope: McpUsageScope;
  range: McpUsageRange;
  granularity: McpUsageGranularity;
  team: string | undefined;
  email: string | undefined;
} {
  return {
    scope: search.scope ?? MCP_USAGE_SEARCH_DEFAULTS.scope,
    range: search.range ?? MCP_USAGE_SEARCH_DEFAULTS.range,
    granularity: search.granularity ?? MCP_USAGE_SEARCH_DEFAULTS.granularity,
    team: search.team,
    email: search.email,
  };
}

/**
 * McpUsageSearch を URL search の sparse 表現に変換する。default 値は出さない。
 * scope に合わない team/email は URL に残さない。
 * @graph-connects none
 */
export function stripMcpUsageSearchDefaults(search: McpUsageSearch): McpUsageSearch {
  const resolved = resolveMcpUsageSearch(search);
  const out: McpUsageSearch = {};
  if (resolved.scope !== MCP_USAGE_SEARCH_DEFAULTS.scope) out.scope = resolved.scope;
  if (resolved.range !== MCP_USAGE_SEARCH_DEFAULTS.range) out.range = resolved.range;
  if (resolved.granularity !== MCP_USAGE_SEARCH_DEFAULTS.granularity) {
    out.granularity = resolved.granularity;
  }
  if (resolved.scope === 'team' && resolved.team) out.team = resolved.team;
  if (resolved.scope === 'member' && resolved.email) out.email = resolved.email;
  return out;
}
